import type { WorkspaceApp } from "@bafe/types";
import { Outlet, useLocation, useParams } from "react-router";

import {
	MANAGE_EMAIL_LABEL,
	MANAGE_EMAIL_TABS,
	MANAGE_PHONE_LABEL,
	MANAGE_PHONE_TABS,
	MANAGE_PRIMARY_LABEL,
	MANAGE_PRIMARY_TABS,
	manageEmailTabId,
	managePhoneTabId,
	managePrimaryTabId,
	type ManagePrimaryTabId,
} from "../lib/manage-navigation.js";
import { useSession } from "../lib/session.js";
import { workspaceHref } from "../lib/workspace-url.js";
import { TabNav } from "./TabNav.js";

function visiblePrimaryTabs(input: {
	showEmail: boolean;
	showPhone: boolean;
	showTeam: boolean;
}): ManagePrimaryTabId[] {
	return MANAGE_PRIMARY_TABS.filter((id) => {
		if (id === "email") return input.showEmail;
		if (id === "phone") return input.showPhone;
		if (id === "team") return input.showTeam;
		return true;
	});
}

function primaryTabHref(workspaceSlug: string, tab: ManagePrimaryTabId): string {
	switch (tab) {
		case "overview":
			return workspaceHref(workspaceSlug, "manage");
		case "access":
			return workspaceHref(workspaceSlug, "manage/access");
		case "domains":
			return workspaceHref(workspaceSlug, "manage/domains");
		case "billing":
			return workspaceHref(workspaceSlug, "manage/billing");
		case "integration":
			return workspaceHref(workspaceSlug, "manage/integration");
		case "activity":
			return workspaceHref(workspaceSlug, "manage/activity");
		case "email":
			return workspaceHref(workspaceSlug, "manage/email/mailboxes");
		case "phone":
			return workspaceHref(workspaceSlug, "manage/phone/numbers");
		case "team":
			return workspaceHref(workspaceSlug, "manage/team");
	}
}

export function ManageLayout() {
	const { workspaceSlug = "" } = useParams();
	const location = useLocation();
	const session = useSession();
	const apps = new Set((session.data?.apps ?? []) as WorkspaceApp[]);
	const showEmail = apps.has("mail");
	const showPhone = apps.has("hub");
	const showTeam = apps.has("team");

	const primaryId = managePrimaryTabId(location.pathname);
	const primaryTabs = visiblePrimaryTabs({ showEmail, showPhone, showTeam });
	const currentPrimary =
		primaryId && primaryTabs.includes(primaryId) ? primaryId : "__none__";

	const ws = (path: string) => workspaceHref(workspaceSlug, path);

	return (
		<div className="flex flex-col gap-6">
			<TabNav
				label="Manage"
				currentId={currentPrimary}
				items={primaryTabs.map((id) => ({
					id,
					label: MANAGE_PRIMARY_LABEL[id],
					to: primaryTabHref(workspaceSlug, id),
				}))}
			/>
			{primaryId === "email" && showEmail ? (
				<TabNav
					label="Email"
					currentId={manageEmailTabId(location.pathname)}
					items={MANAGE_EMAIL_TABS.map((id) => ({
						id,
						label: MANAGE_EMAIL_LABEL[id],
						to: ws(`manage/email/${id}`),
					}))}
				/>
			) : null}
			{primaryId === "phone" && showPhone ? (
				<TabNav
					label="Phone"
					currentId={managePhoneTabId(location.pathname)}
					items={MANAGE_PHONE_TABS.map((id) => ({
						id,
						label: MANAGE_PHONE_LABEL[id],
						to: ws(`manage/phone/${id}`),
					}))}
				/>
			) : null}
			<Outlet />
		</div>
	);
}
